import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const capitalizeFirst = (str) =>
  str && typeof str === "string" && str.length
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : str;

function EditableTitle({
  title,
  onEdit,
  titleFont,
  size = null,
  readOnly = false,
  align = "center",
  fullWidth = false,
}) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const displayTitle = capitalizeFirst(title);
  let fontSize = "1rem";
  let fontWeight = 400;

  if (titleFont === "titleCatalog") {
    fontSize = "1.35rem";
    fontWeight = 700;
  } else if (titleFont === "title") {
    fontSize = size ? `${size}px` : "1.25rem";
    fontWeight = size ? 600 : 700;
  } else if (titleFont === "subtitleCatalog") {
    fontSize = "0.9375rem";
    fontWeight = 700;
  } else if (titleFont === "subtitle") {
    fontSize = "0.9375rem";
    fontWeight = 600;
  } else if (titleFont === "body") {
    fontSize = "0.875rem";
    fontWeight = 400;
  }

  useEffect(() => {
    if (!isEditing) setCurrentTitle(title);
  }, [title, isEditing]);

  const save = () => {
    setIsEditing(false);
    onEdit(currentTitle);
  };

  const inCard = titleFont === "subtitle" || titleFont === "subtitleCatalog";
  const isCatalogTitle = titleFont === "titleCatalog";
  const wide = fullWidth || inCard || !titleFont || isCatalogTitle;
  const dashedColor = theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : "#c5c9d1";
  const idleCheckBg = theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e6e8ec";
  const idleCheckColor = theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280";
  const titleColor = isCatalogTitle ? theme.palette.primary.main : "text.primary";

  const frameSx = {
    display: "flex",
    alignItems: inCard ? "flex-start" : "center",
    gap: 1,
    // Catalog + card titles should fill their container so names stay readable.
    width: wide || isCatalogTitle ? "100%" : "fit-content",
    maxWidth: "100%",
    boxSizing: "border-box",
    border: "1px dashed",
    borderColor: isCatalogTitle ? alpha(theme.palette.primary.main, 0.45) : dashedColor,
    borderRadius: 2,
    mt: inCard ? 1.25 : 0,
    px: inCard ? 1.25 : isCatalogTitle ? 1.75 : 1.25,
    py: inCard ? 0.75 : isCatalogTitle ? 0.9 : 0.75,
  };
  const titleTextSx = {
    flex: wide || isCatalogTitle ? 1 : "0 0 auto",
    minWidth: 0,
    textAlign: align,
    fontSize,
    fontWeight,
    color: titleColor,
    letterSpacing: isCatalogTitle ? "-0.02em" : undefined,
    lineHeight: 1.35,
    overflow: "hidden",
    // Card titles wrap up to 2 lines; other titles stay single-line ellipsis.
    ...(inCard
      ? {
          whiteSpace: "normal",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
        }
      : {
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }),
  };

  const stopCardNav = (e) => {
    // Collection / embedded cards put navigation on the parent; keep
    // title clicks here so renaming does not open showTitle.
    e?.stopPropagation?.();
  };

  return (
    <MDBox sx={frameSx} onClick={stopCardNav} onMouseDown={stopCardNav}>
      {isEditing ? (
        <Box
          component="input"
          type="text"
          value={currentTitle}
          onChange={(e) => setCurrentTitle(e.target.value)}
          onBlur={save}
          onClick={stopCardNav}
          onMouseDown={stopCardNav}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") {
              setCurrentTitle(title);
              setIsEditing(false);
            }
          }}
          autoFocus
          aria-label="Title"
          sx={{
            ...titleTextSx,
            border: 0,
            outline: "none",
            appearance: "none",
            WebkitAppearance: "none",
            colorScheme: "light",
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#fff",
            backgroundColor: `${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#fff"} !important`,
            color: `${theme.palette.mode === "dark" ? "#f8fafc" : isCatalogTitle ? theme.palette.primary.main : "#1e293b"} !important`,
            WebkitTextFillColor: theme.palette.mode === "dark" ? "#f8fafc" : isCatalogTitle ? theme.palette.primary.main : "#1e293b",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            m: 0,
            fontFamily: "inherit",
            width: wide && !isCatalogTitle ? undefined : `${Math.max(String(currentTitle || "").length, 1)}ch`,
            fieldSizing: "content",
          }}
        />
      ) : (
        <MDTypography
          variant="h6"
          onClick={(e) => {
            stopCardNav(e);
            if (!readOnly) setIsEditing(true);
          }}
          sx={{
            ...titleTextSx,
            cursor: readOnly ? "default" : "pointer",
          }}
        >
          {displayTitle}
        </MDTypography>
      )}
      {!readOnly && (
        <IconButton
          aria-label={isEditing ? "Save title" : "Edit title"}
          onMouseDown={(e) => {
            e.preventDefault();
            stopCardNav(e);
          }}
          onClick={(e) => {
            stopCardNav(e);
            if (isEditing) save();
            else setIsEditing(true);
          }}
          sx={{
            flexShrink: 0,
            width: inCard ? 30 : 36,
            height: inCard ? 30 : 36,
            mt: inCard ? 0.15 : 0,
            borderRadius: 1.5,
            bgcolor: isEditing ? theme.palette.success.main : idleCheckBg,
            color: isEditing ? "#fff" : idleCheckColor,
            "&:hover": {
              bgcolor: isEditing ? theme.palette.success.focus || theme.palette.success.main : theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "#d5d8de",
            },
          }}
        >
          <Icon fontSize="small">check</Icon>
        </IconButton>
      )}
    </MDBox>
  );
}

export default EditableTitle;
